import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function RatingPromptModal({ consultationId, lawyerName, open, onOpenChange, onSubmitted }) {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Prefill existing review if any
    api.get(`/consultations/${consultationId}/review`).then((r) => {
      if (r.data?.rating) {
        setRating(r.data.rating);
        setText(r.data.text || "");
      }
    }).catch(() => {});
  }, [open, consultationId]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/consultations/${consultationId}/review`, { rating, text });
      toast.success("Thanks for your review!");
      onSubmitted?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="rating-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>How was your consultation with {lawyerName}?</DialogTitle>
          <DialogDescription>
            Your feedback helps other Indians find the right lawyer. Ratings are public (first-name only).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="flex justify-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => {
              const active = (hover || rating) >= n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  data-testid={`star-${n}`}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star size={38} className={active ? "text-[#D4AF37]" : "text-slate-200"} fill={active ? "currentColor" : "none"} strokeWidth={1.5} />
                </button>
              );
            })}
          </div>
          <Textarea
            data-testid="review-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Share a few words about your experience (optional)…"
            className="min-h-[100px]"
          />
          <Button
            onClick={submit}
            disabled={submitting || !rating}
            data-testid="submit-review-btn"
            className="w-full rounded-full adv-btn-gold font-bold"
          >
            {submitting ? "Submitting…" : "Submit review"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
